import "./schedule-report-modal.scss";

import { Close } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogProps,
  Grid,
  IconButton,
} from "@mui/material";
import Modal from "../modal/modal";
import Typography from "../typography/typography";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import Button from "../button/button";

import DynamicInput from "../form-control/form-builder/form-builder-element/text";
import DynamicDate from "../form-control/form-builder/form-builder-element/date";
import DynamicTime from "../form-control/form-builder/form-builder-element/time";
import DynamicCheckBox from "../form-control/form-builder/form-builder-element/checkbox";
import dayjs from "dayjs";
import { scheduleReportValidationSchema } from "./validator";
import DynamicSearchSelect from '../form-control/form-builder/form-builder-element/dynamic-select';
import React from "react";
import ChipGenerator from "../chip-generator/chip-generator";
import { formatDateForPayload, formatTimeForPayload, getErrorMessage, getToken, mapApiPayload } from "../../utils/common";
import { enqueueSnackbar } from "notistack";
import { AUTO_POST_OPTIONS, createScheduleReportType, getScheduleReportById, updateScheduleReportType } from "./utils/common";
import ModalLoader from "../loaders/modal-loader";
import { apiPayload, defaultValues, formArrayName } from "./utils/constant";
interface ScheduleReportModalProps {
  isOpen?: boolean;
  isNewScheduleReport?: boolean;
  modalProps?: DialogProps;
  itemData?: any;
  onSubmit?: () => void;
  onClose: () => void;
  label?: string;
  module?: string;
  filters?: any;
  report?: string;
}

const ScheduleReportModal: React.FC<ScheduleReportModalProps> = ({
  label,
  isOpen = false,
  modalProps,
  itemData,
  onSubmit,
  onClose,
  module = 'accounting',
  filters,
  report = null
}) => {
  //Local States
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  //Form Instance
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { dirtyFields }
  } = useForm({
    resolver: scheduleReportValidationSchema(!itemData?.id),
    mode: "all",
    // shouldUnregister: true,
    defaultValues: defaultValues
  });

  const isRecurring = watch(`${formArrayName}.is_recurring`)

  const disableReport = useMemo(() => {
    return (itemData?.id || report) ? true : false;
  }, [itemData?.id, report]);

  const handleFormSubmit = async (vals) => {
    const isValid = await trigger();
    if (!isValid) return;
    const values = vals?.[formArrayName];
    // setSubmitLoading(true);
    const valuesPayload = {
      ...values,
      type: module,
      filters,
      schedule_date: formatDateForPayload(values.schedule_date),
      schedule_time: formatTimeForPayload(values.schedule_time),
      ...(itemData && itemData?.id
        ? { id: String(itemData.id) || "" }
        : {}),
    };
    delete valuesPayload?.email;

    const updatedPayload = mapApiPayload(apiPayload, valuesPayload)
    const apiFunc = itemData?.id ? updateScheduleReportType : createScheduleReportType
    try {
      await apiFunc({
        ...updatedPayload,
        ...getToken(),
      })
      onSubmit?.()
      enqueueSnackbar("Report scheduled successfully", {});
      handleModalClose()

    } catch (error) {
      const errorMessage = getErrorMessage(error?.message)
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      setSubmitLoading(false);
    }
    //eslint-disable-next-line
  };


  const handleModalClose = () => {
    onClose();
    setTimeout(() => {
      reset({
        [formArrayName]: {}
      });
    }, 300);
  };

  const fetchReportByName = useCallback(async () => {
    if (!isOpen || !report) {
      return
    }
    try {
      const { data = {} } = await getScheduleReportById({
        ...getToken(),
        filters: `(name.like=%${report}%)`
      })
      setValue(`${formArrayName}.report_id`, data?.reports?.[0]?.id)
    } catch (error) {
      console.log("error", error)
    }
    //eslint-disable-next-line
  }, [report, isOpen, setValue])
  //Effects
  useEffect(() => {

    const time = itemData?.schedule_time?.split(":")?.map(Number);
    if (itemData?.id) {
      reset({
        [formArrayName]: {
          ...itemData,
          schedule_date: itemData?.schedule_date
            ? dayjs(itemData?.schedule_date)
            : "",
          schedule_time:
            time?.length === 3
              ? dayjs()
                .set("hour", time[0])
                .set("minute", time[1])
                .set("second", time[2])
                .set("millisecond", 0)
              : "",
        }
      });
    } else {
      reset(defaultValues)
    }
    //eslint-disable-next-line
  }, [isOpen, itemData?.id, module]);


  const scheduleDate = watch(`${formArrayName}.schedule_date`);

  useEffect(() => {
    if (scheduleDate && dirtyFields[formArrayName]?.schedule_date) {
      trigger(`${formArrayName}.schedule_time`);
    }
  }, [scheduleDate, trigger, dirtyFields]);

  const isToday = useMemo(() => {
    return scheduleDate && dayjs(scheduleDate).startOf("day").isSame(dayjs().startOf("day"));
  }, [scheduleDate]);

  useEffect(() => {
    fetchReportByName()
  }, [fetchReportByName]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      {...modalProps}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} id="scheduleReportModal">
        {(submitLoading) && <ModalLoader />}
        <DialogTitle className="scheduleReportModal--Title">
          <Typography type="s3" color="inherit" weight="medium">
            {label || 'Schedule'}
          </Typography>
          <IconButton onClick={handleModalClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
            <Grid item sm={12}>
              <Grid item sm={6}>
                <DynamicSearchSelect
                  disabled={Boolean(itemData?.id) || disableReport}
                  label="Report"
                  apiType="reports"
                  name="report_id"
                  placeholder="Select report"
                  fieldArrayName={formArrayName}
                  formControl={control}
                  required
                  customeFilter={{
                    '&type.eq': module,
                    '&allow_scheduling.eq': 1
                  }}
                />
              </Grid>
            </Grid>
            <Grid item sm={6}>
              <DynamicInput
                type="text"
                formControl={control}
                label="Report name"
                name="name"
                fieldArrayName={formArrayName}
                placeholder="Enter Report Name"
                required
              />
            </Grid>
            <Grid item sm={6}>
              <DynamicInput
                type="text"
                formControl={control}
                label="Subject"
                name="subject"
                fieldArrayName={formArrayName}
                placeholder="Enter Subject"
              />
            </Grid>
            <Grid item sm={6}>
              <DynamicDate
                formControl={control}
                name="schedule_date"
                placeholder="DD-MM-YYYY"
                label="Date"
                fieldArrayName={formArrayName}
                required
                min_date={dayjs()}
              />
            </Grid>
            <Grid item sm={6}>
              <DynamicTime
                formControl={control}
                placeholder="hh:mm"
                label="Time"
                option={"24hr"}
                name="schedule_time"
                fieldArrayName={formArrayName}
                required
                rest={{
                  timeSteps: { minutes: 1 },
                  ampm: false,
                  ...(isToday && dirtyFields[formArrayName]?.schedule_time && { minTime: dayjs() }),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ChipGenerator
                control={control}
                name="emails"
                label="Email"
                required
                fieldArrayName={formArrayName}
                placeholder="Enter Emails: example1@mail.com, example2@mail.com"
                validation={[{
                  type: 'email',
                  message: 'Please enter a valid email address',
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                }]}
              />
            </Grid>
            <Grid item sm={12}>
              <DynamicCheckBox
                formControl={control}
                name="is_recurring"
                title="Recurring"
                fieldArrayName={formArrayName}
                titlePosition="end"
              />
            </Grid>
            {isRecurring ? (
              <Grid item sm={6}>
                <DynamicSearchSelect
                  label="Auto Post"
                  options={AUTO_POST_OPTIONS || []}
                  name="autopost"
                  placeholder="Select Auto Post"
                  fieldArrayName={formArrayName}
                  formControl={control}
                  required
                />
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions className="scheduleReportModal--ActionContainer">
          <Box display="flex" gap={1.5}>
            <Button
              className="scheduleReportModal--ActionContainer--WhiteBtn"
              onClick={handleModalClose}
              disabled={submitLoading}
            >
              Discard
            </Button>
            <Button
              type="button"
              onClick={handleSubmit(handleFormSubmit)}
              endIcon={
                submitLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
              disabled={submitLoading}
            >
              Schedule
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Modal>
  );
};

export { ScheduleReportModal };

export default ScheduleReportModal;
